// multiRecord.js — pure schedule→active-set helper for MultiRecord++ (V0.5).
//
// We no longer ship a custom recorder. The actual capture/upload is done by the
// PROVEN classic recorder (api/Recorder.js AudioRecorder), driven by
// <MultiRecordEngine/> (components/ComprehendLive/MultiRecordEngine.js). This
// module only computes WHICH visits should be recording right now from the
// schedule + clock + the clinician's manual overrides — used by the engine
// (to set the recorder's targets) and by the timeline/rail (to highlight).
//
// Design: docs/comprehend-live-v05.md.

export const SWITCH_OVERLAP_MS = 5 * 60 * 1000; // 5-min handoff overlap

/**
 * Pure: which conversationIds should be recording right now, from the
 * schedule + clock + the clinician's manual overrides.
 *
 * A slot is active from its start until OVERLAP past its end, so at a
 * transition the outgoing visit stays live a few minutes into the next —
 * splitTranscripts attributes the handoff correctly. Manual overrides win:
 * `manualOn` force-includes, `manualOff` force-excludes (until cleared).
 */
export const computeActiveSet = (
  schedule,
  nowMs,
  overrides = {},
  buffers = {},
) => {
  // Customizable record window: start `beforeMs` before the scheduled start
  // (lead-in) and keep going `afterMs` past the scheduled end (handoff).
  const beforeMs = Number.isFinite(buffers.beforeMs) ? buffers.beforeMs : 0;
  const afterMs = Number.isFinite(buffers.afterMs)
    ? buffers.afterMs
    : SWITCH_OVERLAP_MS;
  // Auto mode (off by default): only when ON does the schedule clock pick which
  // visits record (within the buffer window). Off => purely manual: just the
  // visits the clinician tapped (manual overrides below).
  const auto = buffers.auto === true;
  const ids = new Set();
  if (auto) {
    for (const slot of schedule || []) {
      const start = new Date(slot.start).getTime();
      const end = new Date(slot.end).getTime();
      if (Number.isNaN(start) || Number.isNaN(end)) continue;
      for (const p of slot.patients || []) {
        const patientEnd = p.end == null ? NaN : new Date(p.end).getTime();
        const effectiveEnd = Number.isNaN(patientEnd) ? end : patientEnd;
        if (nowMs >= start - beforeMs && nowMs < effectiveEnd + afterMs) {
          if (p.conversationId != null) ids.add(String(p.conversationId));
        }
      }
    }
  }
  const { manualOn = [], manualOff = [] } = overrides;
  manualOn.forEach((id) => ids.add(String(id)));
  manualOff.forEach((id) => ids.delete(String(id)));
  return [...ids];
};
